//libs
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import ReactSelect from "react-select";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import PhoneInput from "react-phone-number-input";
//components
import Button from "components/Button";
import Message from "components/Message";
import Container from "components/Container";
import Loading from "components/Loading";
//hooks
import { useSuppliers } from "hooks/api/useSuppliers";
import { useSupplierBusinessTypes } from "hooks/api/useSupplierBusinessTypes";
import { useSupplierIndustries } from "hooks/api/useSupplierIndustries";
import CTAHero from "features/home/components/CTAHero";

const schema = yup.object({
  businessType: yup.string().required("Business Type is required"),
  individualContactFullName: yup.string().required("FullName is required"),
  individualContactDesignation: yup
    .string()
    .required("Designation is required"),
  individualContactPhone: yup.string().required("Phone is required"),
  individualContactEmail: yup
    .string()
    .email("Email must be email format")
    .required("Email is required"),
  individualContactAddress: yup.string().required("Address is required"),
  individualSupplierIndustries: yup
    .array()
    .of(yup.string().required())
    .required("Supplier Industries are required"),
});

export default function SupplierRegistrationIndividual() {
  const { data } = useSupplierBusinessTypes();
  const supplierBusinessTypes = data?.data;
  const { supplierIndustries } = useSupplierIndustries();
  const { isLoading, createIndividualSupplier } = useSuppliers();
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (supplierBusinessTypes?.length > 0) {
      setValue("businessType", supplierBusinessTypes[0].name);
    }
  }, [supplierBusinessTypes, setValue]);

  const onSubmit = (values) => {
    try {
      createIndividualSupplier({
        business_type: values.businessType,
        individual_contact_full_name: values.individualContactFullName,
        individual_contact_designation: values.individualContactDesignation,
        individual_contact_phone: values.individualContactPhone,
        individual_contact_email: values.individualContactEmail,
        individual_contact_address: values.individualContactAddress,
        supplier_industries: values.individualSupplierIndustries,
      });
    } catch (err) {}
  };

  return (
    <div>
      <CTAHero />
      <Container className="mt-8">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-4 border border-slate-300 rounded-md max-w-5xl mx-auto px-12"
        >
          <div className="mt-6 flex items-center justify-between gap-x-6">
            <h3 className="text-2xl font-bold">Create Supplier - Individual</h3>
            <div className="flex gap-4">
              <Button disabled={isLoading} type="submit">
                <Loading text="Register" isLoading={isLoading} />
              </Button>
              <Button
                href="/supplier-register"
                color="white"
                className="text-sm font-semibold leading-6 text-gray-900"
              >
                Cancel
              </Button>
            </div>
          </div>

          <div className="border-b border-purple-200"></div>

          <div className="sm:col-span-3">
            <label
              htmlFor="businessType"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              Business Type<span className="text-red-600">*</span>
            </label>
            <div className="mt-2">
              <select
                id="businessType"
                name="businessType"
                autoComplete="off"
                {...register("businessType")}
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:max-w-xs sm:text-sm sm:leading-6"
              >
                {supplierBusinessTypes?.map((type) => (
                  <option key={type.id} value={type.name}>
                    {type.name}
                  </option>
                ))}
              </select>
              <Message message={errors.businessType?.message} />
            </div>
          </div>

          <div className="border-b border-gray-900/10 pb-12">
            <h2 className="text-base font-semibold leading-7 text-gray-900">
              Personal Information
            </h2>

            <div className="mt-4 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6">
              <div className="sm:col-span-3">
                <label
                  htmlFor="full-name"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Full Name<span className="text-red-600">*</span>
                </label>
                <div className="mt-2">
                  <input
                    id="full-name"
                    name="full-name"
                    type="text"
                    {...register("individualContactFullName")}
                    autoComplete="off"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6"
                  />
                  <Message
                    message={errors.individualContactFullName?.message}
                  />
                </div>
              </div>

              <div className="sm:col-span-3">
                <label
                  htmlFor="designation"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Designation<span className="text-red-600">*</span>
                </label>
                <div className="mt-2">
                  <input
                    id="designation"
                    name="designation"
                    type="text"
                    {...register("individualContactDesignation")}
                    autoComplete="off"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6"
                  />
                  <Message
                    message={errors.individualContactDesignation?.message}
                  />
                </div>
              </div>

              <div className="sm:col-span-3">
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Phone Number<span className="text-red-600">*</span>
                </label>
                <div className="mt-2">
                  <input
                    id="phone"
                    name="phone"
                    type="number"
                    {...register("individualContactPhone")}
                    autoComplete="off"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6"
                  />
                  {/* <Controller
                  name="individualContactPhone"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <PhoneInput
                      {...field}
                      defaultCountry="KH"
                      initialValueFormat="national"
                      onChange={(value) => field.onChange(value)}
                      value={field.value}
                    />
                  )}
                /> */}
                  <Message message={errors.individualContactPhone?.message} />
                </div>
              </div>

              <div className="sm:col-span-3">
                <label
                  htmlFor="email"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Email address<span className="text-red-600">*</span>
                </label>
                <div className="mt-2">
                  <input
                    id="email"
                    name="email"
                    type="text"
                    {...register("individualContactEmail")}
                    autoComplete="off"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6"
                  />
                  <Message message={errors.individualContactEmail?.message} />
                </div>
              </div>

              <div className="col-span-full">
                <label
                  htmlFor="street-address"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Address<span className="text-red-600">*</span>
                </label>
                <div className="mt-2">
                  <input
                    id="street-address"
                    name="street-address"
                    type="text"
                    {...register("individualContactAddress")}
                    autoComplete="off"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6"
                  />
                  <Message message={errors.individualContactAddress?.message} />
                </div>
              </div>

              <div className="col-span-full">
                <label
                  htmlFor="supplier-industries"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Supplier Industries<span className="text-red-600">*</span>
                </label>
                <div className="mt-2">
                  <Controller
                    id="supplier-industries"
                    name="individualSupplierIndustries"
                    control={control}
                    render={({ field }) => (
                      <ReactSelect
                        {...field}
                        isMulti
                        options={supplierIndustries}
                        className="basic-multi-select"
                        classNamePrefix="select"
                        placeholder="Select supplier industries(s)"
                        value={supplierIndustries?.filter((option) =>
                          field?.value?.includes(option?.value)
                        )}
                        onChange={(selectedOptions) => {
                          field?.onChange(
                            selectedOptions.map((option) => option?.value)
                          );
                        }}
                      />
                    )}
                  />
                  <Message
                    message={errors.individualSupplierIndustries?.message}
                  />
                </div>
              </div>
            </div>
          </div>
        </form>
      </Container>
    </div>
  );
}
